/**
 * Sistem Warna Terpusat - Booku v2
 *
 * PENTING: Semua warna harus didefinisikan di sini.
 * Jangan hardcode warna seperti '#388E3C' di komponen lain.
 *
 * Penggunaan:
 * - Import: import { colors } from '@/theme/colors';
 * - Akses: colors.primary, colors.primaryDark, dll
 */

// =============================================================================
// WARNA UTAMA (Brand Colors)
// =============================================================================

export const colors = {
  // Primary - Hijau Booku
  primary: '#388E3C',
  primaryDark: '#2E7D32',
  primaryLight: '#4CAF50',
  primarySurface: '#E8F5E9',
  primaryHover: '#2E7D32',

  // Secondary - Abu-abu
  secondary: '#757575',
  secondaryDark: '#616161',
  secondaryLight: '#9E9E9E',

  // Accent - Kuning (untuk highlight)
  accent: '#FFD54F',
  accentDark: '#FFC107',
  accentLight: '#FFECB3',

  // =============================================================================
  // WARNA SEMANTIK (Semantic Colors)
  // =============================================================================

  // Success - Hijau
  success: '#4CAF50',
  successDark: '#388E3C',
  successLight: '#C8E6C9',
  successSurface: '#E8F5E9',

  // Warning - Kuning/Orange
  warning: '#FF9800',
  warningDark: '#F57C00',
  warningLight: '#FFE0B2',
  warningSurface: '#FFF3E0',

  // Error/Danger - Merah
  error: '#F44336',
  errorDark: '#D32F2F',
  errorLight: '#FFCDD2',
  errorSurface: '#FFEBEE',

  // Info - Biru
  info: '#2196F3',
  infoDark: '#1976D2',
  infoLight: '#BBDEFB',
  infoSurface: '#E3F2FD',

  // =============================================================================
  // WARNA NETRAL (Neutral Colors)
  // =============================================================================

  // Background
  bgPrimary: '#FFFFFF',
  bgSecondary: '#F5F5F5',
  bgTertiary: '#EEEEEE',
  bgDark: '#212121',

  // Text
  textPrimary: '#212121',
  textSecondary: '#757575',
  textDisabled: '#BDBDBD',
  textInverse: '#FFFFFF',

  // Border
  border: '#E0E0E0',
  borderLight: '#EEEEEE',
  borderDark: '#BDBDBD',

  // Divider
  divider: '#E0E0E0',

  // =============================================================================
  // WARNA KHUSUS AKUNTANSI
  // =============================================================================

  // Debit/Kredit
  debit: '#1976D2',   // Biru
  kredit: '#388E3C',  // Hijau

  // Saldo
  saldoPositif: '#388E3C',  // Hijau
  saldoNegatif: '#D32F2F',  // Merah

  // Status Transaksi
  statusDraft: '#9E9E9E',
  statusPosted: '#388E3C',
  statusVoid: '#D32F2F',
  statusPending: '#FF9800',

  // =============================================================================
  // INPUT / TEXTBOX
  // =============================================================================

  // Background untuk input MUA (Mata Uang Asal) & Kurs — biru muda agar mudah
  // dibedakan dari input IDR biasa. Konsisten dengan Booku Lama.
  mataUangBg: '#E3F2FD',

  // Varian buram untuk input MUA/Kurs saat disabled — tetap mempertahankan
  // identitas warna biru MUA, namun lebih lembut sebagai indikasi tidak aktif.
  mataUangBgDisabled: '#EDF4FA',
} as const;

// =============================================================================
// GRADIENTS
// =============================================================================

export const gradients = {
  primaryGradient: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
  successGradient: `linear-gradient(135deg, ${colors.success} 0%, ${colors.successDark} 100%)`,
  warningGradient: `linear-gradient(135deg, ${colors.warning} 0%, ${colors.warningDark} 100%)`,
  errorGradient: `linear-gradient(135deg, ${colors.error} 0%, ${colors.errorDark} 100%)`,
} as const;

// =============================================================================
// SHADOWS
// =============================================================================

export const shadows = {
  sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px rgba(0, 0, 0, 0.1)',
  lg: '0 4px 12px rgba(0, 0, 0, 0.15)',
  xl: '0 8px 24px rgba(0, 0, 0, 0.2)',
} as const;

// Type exports for TypeScript
export type ColorKey = keyof typeof colors;
export type GradientKey = keyof typeof gradients;
export type ShadowKey = keyof typeof shadows;
